// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  CircularProgress,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Snackbar,
  IconButton,
  Dialog, DialogActions, DialogContent,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import moment from 'moment-timezone';
import HelpIcon from '@mui/icons-material/Help';
import Alert from '@mui/material/Alert';
import { useAppDispatch, useAppSelector } from 'Hooks';
import downloadIcon from 'Assets/download-icon.png';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import {
  DownloadLASThunk as DownloadLASAction,
  DownloadSurfaceFileThunk as DownloadSurfaceFileAction,
  DownloadDeliverableFileThunk as DownloadDeliverableFileAction,
  DownloadPostgisDxfThunk as DownloadPostgisDxfAction,
  SetDownloadFilesState as SetDownloadFilesStateAction,
  ClearDownloadFilesThunk as ClearDownloadFilesAction,
  SetGeojsonFilesState as SetGeoJsonDownloadStatusAction,
  DownloadGeoDbFileThunk as DownloadGeoDbFileAction,
  DownloadShpFileThunk as DownloadShpFileAction,
  GetDxfDirectUrlThunk as GetDxfDirectUrlAction,
} from 'Features/order/orderThunk';
import { WideLightTooltip } from 'Components/Tooltip';
import stylesDef from './styles';
import isS3PresignedUrlExpired from 'Utils/validateUrl';

moment.tz.setDefault('America/New_York');

interface ComponentProps {
  open: boolean;
  order: IOrder;
  fileName: string;
  siteId: string;
  handleCloseDownloadDialog: () => void;
  cadFiles: {
    fileName: string;
    siteId: string;
    expressAI: boolean;
    visible: boolean;
}[];
}

export const DownloadPanel: React.FC<ComponentProps> = ({ open, fileName, siteId, order, handleCloseDownloadDialog, cadFiles }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const id = open ? 'simple-popper' : null;
  const { deliveryEstimate, expressAICreditAcreage } = order;
  const orderId = order._id;
  const orderedGeoJson = order?.addOns?.some((el) => el.name === 'Geojson File Format (.geojson)');
  const allDownloadFileStatuses = useAppSelector((state) => state.order?.dxfFileStatus);
  const allDownloadGeoJsonFileStatuses = useAppSelector((state) => state.order?.geoJsonFileStatus);
  const anyChangeInFileStatuses = useAppSelector((state) => state.order?.anyChangeInFileStatuses);

  // Filter download states to only include files from the current project
  const downloadFileStatuses = useMemo(() => {
    const currentProjectSiteIds = cadFiles.map(file => file?.siteId);
    return allDownloadFileStatuses.filter((fileStatus: any) => 
      currentProjectSiteIds.includes(fileStatus.siteId)
    );
  }, [allDownloadFileStatuses, cadFiles]);

  const downloadGeoJsonFileStatuses = useMemo(() => {
    const currentProjectSiteIds = cadFiles.map(file => file?.siteId);
    return allDownloadGeoJsonFileStatuses.filter((fileStatus: any) => 
      currentProjectSiteIds.includes(fileStatus.siteId)
    );
  }, [allDownloadGeoJsonFileStatuses, cadFiles]);

  const [includeEmptyLayers, setIncludeEmptyLayers] = useState<{ [key: string]: boolean }>({});
  const [showExpressAIWarning, setShowExpressAIWarning] = useState(false);
  const [dialogSpinner, setDialogSpinner] = useState(false);
  const [dxfFileLoadingState, setDxfFileLoadingState] = useState<{ [fileName: string]: string }>({});
  const [geoJsonFileLoadingState, setGeoJsonFileLoadingState] = useState<{ [fileName: string]: string }>({});
  const [fileDownloadCheckboxState, setFileDownloadCheckboxState] = useState({
    dxfFile: false,
    lasFile: false,
    surfaceFile: false,
    geoDbFile: false,
    geoJson: false,
    shapeFile: false,
    demFile: false,
    dtmFile: false,
    dwgFile: false,
    dgnFile: false,
    zipFile: false,
    rarFile: false,
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [cadFile, setCadFile] = useState<ICadFile | undefined>(undefined);

  // Memoized file filters
  const demFiles = useMemo(() => order?.deliverableFiles?.filter((file) => /\.dem$/.test(file.filename)) ?? [], [order?.deliverableFiles]);
  const dtmFiles = useMemo(() => order?.deliverableFiles?.filter((file) => /\.dtm$/.test(file.filename)) ?? [], [order?.deliverableFiles]);
  const dwgFiles = useMemo(() => order?.deliverableFiles?.filter((file) => /\.dwg$/.test(file.filename)) ?? [], [order?.deliverableFiles]);
  const dgnFiles = useMemo(() => order?.deliverableFiles?.filter((file) => /\.dgn$/.test(file.filename)) ?? [], [order?.deliverableFiles]);
  const shapeFiles = order?.shpFiles ?? [];
  const surfaceFiles = order?.surfaceFiles ?? [];
  const geoDbFiles = order?.geoDbFiles ?? [];
  const lasFiles = order?.outputLasFiles ?? [];

  const downloadFileStatusesIndex = useMemo(() => 
    downloadFileStatuses.find((obj: any) => 
      Object.keys(dxfFileLoadingState ?? []).includes(obj.siteId) && 
      dxfFileLoadingState[obj.siteId] === 'Preparing Download'
    ), [downloadFileStatuses, dxfFileLoadingState]);

  const downloadGeoJsonFileStatusesIndex = useMemo(() => 
    downloadGeoJsonFileStatuses.find((obj: any) => 
      Object.keys(geoJsonFileLoadingState ?? []).includes(obj.siteId) && 
      geoJsonFileLoadingState[obj.siteId] === 'Preparing Download'
    ), [downloadGeoJsonFileStatuses, geoJsonFileLoadingState]);

  const isAnyFilePreparing = useMemo(() => {
    const allCadFiles = cadFiles.map(file => file?.siteId);
    const dxfPreparing = Object.entries(dxfFileLoadingState).some(([siteId, status]) => status === 'Preparing Download' && allCadFiles.includes(siteId));
    const geoJsonPreparing = Object.entries(geoJsonFileLoadingState).some(([siteId, status]) => status === 'Preparing Download' && allCadFiles.includes(siteId));
    return dxfPreparing || geoJsonPreparing;
  }, [dxfFileLoadingState, geoJsonFileLoadingState, cadFiles]);

  useEffect(() => {
    if (!downloadFileStatusesIndex?.url && !downloadGeoJsonFileStatusesIndex?.url) {
      return;
    }

    let areAllFilesReady = false;

    // Handle DXF download status
    if (downloadFileStatusesIndex?.url && !downloadGeoJsonFileStatusesIndex?.url) {
      const fileStatuses = downloadFileStatusesIndex.fileReadyStatus;
      const checkboxes: { [key: string]: boolean } = {};

      Object.keys(fileStatuses).forEach((fileType) => {
        if (fileType in fileDownloadCheckboxState) {
          checkboxes[fileType] = true;
        }
      });

      setFileDownloadCheckboxState({ ...fileDownloadCheckboxState, ...checkboxes });
      areAllFilesReady = Object.values(fileStatuses).every((value) => value !== 0);
    }

    // Handle GeoJSON download status
    if (!downloadFileStatusesIndex?.url && downloadGeoJsonFileStatusesIndex?.url) {
      const geoJsonFileStatuses = downloadGeoJsonFileStatusesIndex?.fileReadyStatus;
      const checkboxes: { [key: string]: boolean } = {};

      Object.keys(geoJsonFileStatuses).forEach((fileType) => {
        if (fileType in fileDownloadCheckboxState) {
          checkboxes[fileType] = true;
        }
      });

      setFileDownloadCheckboxState({ ...fileDownloadCheckboxState, ...checkboxes });
      areAllFilesReady = Object.values(geoJsonFileStatuses).every((value) => value !== 0);
    }

    if (areAllFilesReady) {
      setDxfFileLoadingState((prev) => ({
        ...prev,
        [downloadFileStatusesIndex?.siteId]: 'Download',
      }));
      setGeoJsonFileLoadingState((prev) => ({
        ...prev,
        [downloadGeoJsonFileStatusesIndex?.siteId]: 'Download',
      }));
      setShowSnackbar(true);
    }
  }, [downloadFileStatuses, downloadGeoJsonFileStatuses, anyChangeInFileStatuses, downloadFileStatusesIndex, downloadGeoJsonFileStatusesIndex, fileDownloadCheckboxState]);

  // Restore loading state from Redux when component mounts
  // Only restore states for files that belong to the current project
  useEffect(() => {
    const currentProjectSiteIds = cadFiles.map(file => file?.siteId);
    
    // Check for ongoing DXF downloads in Redux state
    downloadFileStatuses.forEach((fileStatus: any) => {
      // Only process files that belong to the current project
      if (!currentProjectSiteIds.includes(fileStatus.siteId)) {
        return;
      }
      
      const fileStatuses = fileStatus.fileReadyStatus;
      const hasOngoingDownloads = Object.values(fileStatuses).some((status: any) => status === 0);
      const areAllFilesReady = Object.values(fileStatuses).every((status: any) => status !== 0);

      if (hasOngoingDownloads && !dxfFileLoadingState[fileStatus.siteId]) {
        setDxfFileLoadingState((prev) => ({
          ...prev,
          [fileStatus.siteId]: 'Preparing Download',
        }));
      } else if (areAllFilesReady && fileStatus.url) {
        // Files are ready - show download button immediately
        setDxfFileLoadingState((prev) => ({
          ...prev,
          [fileStatus.siteId]: 'Download',
        }));
      }
    });

    // Check for ongoing GeoJSON downloads in Redux state
    downloadGeoJsonFileStatuses.forEach((fileStatus: any) => {
      // Only process files that belong to the current project
      if (!currentProjectSiteIds.includes(fileStatus.siteId)) {
        return;
      }
      
      const fileStatuses = fileStatus.fileReadyStatus;
      const hasOngoingDownloads = Object.values(fileStatuses).some((status: any) => status === 0);
      const areAllFilesReady = Object.values(fileStatuses).every((status: any) => status !== 0);
      
      if (hasOngoingDownloads && !geoJsonFileLoadingState[fileStatus.siteId]) {
        setGeoJsonFileLoadingState((prev) => ({
          ...prev,
          [fileStatus.siteId]: 'Preparing Download',
        }));
      } else if (areAllFilesReady && fileStatus.url) {
        // Files are ready - show download button immediately
        setGeoJsonFileLoadingState((prev) => ({
          ...prev,
          [fileStatus.siteId]: 'Download',
        }));
      }
    });
  }, [downloadFileStatuses, downloadGeoJsonFileStatuses, cadFiles, dxfFileLoadingState, geoJsonFileLoadingState]);

  const cadPresent = !!(order.cadFiles[0]);

  const confirmDxfExport = async () => {
    setDialogSpinner(true);
    await dispatch(SetDownloadFilesStateAction(siteId, { ...fileDownloadCheckboxState, dxfFile: true, geoJson: false }));
    await dispatch(DownloadPostgisDxfAction(orderId, siteId, includeEmptyLayers[cadFile?._id], { ...fileDownloadCheckboxState, dxfFile: true, geoJson: false }));
    setDialogSpinner(false);
    setShowExpressAIWarning(false);
    setCadFile(undefined);
  };

  const handleIncludeEmptyLayersCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIncludeEmptyLayers((prev) => ({ ...prev, [event.target.name]: checked }));
  };

  const handleClose = (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowSnackbar(false);
  };

  const getFileName = (filename: string) => filename?.split('/')?.pop() ?? '';

  const handleFileDownload = async (
    fileKey: string,
    fileType: 'dxfFile' | 'geoJson',
    fileStatuses: any[],
    setLoadingState: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>,
    clearAction: (fileKey: string, type: string) => any,
    trackingEvent: string,
    additionalTrackingProps?: Record<string, any>
  ) => {
    const file = fileStatuses.find((obj: any) => obj.siteId === fileKey);
    setLoadingState((prev) => ({ ...prev, [fileKey]: 'Prepare' }));
    await dispatch(clearAction(fileKey, fileType));
    
    if (!isS3PresignedUrlExpired(file?.url)) {
      setIncludeEmptyLayers((prev) => ({ ...prev, [fileKey]: false }));
      window.open(file?.url, '_self');
      
      const trackingData = {
        filename: getFileName(file?.url),
        filesize: `${(file?.size ?? 0 / 1000000).toFixed(2)} MB`,
        projectId: order.ownerProject,
        ...additionalTrackingProps,
      };
      
      (window as any)?.pendo?.track(trackingEvent, trackingData);
    }
  };

  const handlePrepareAndDownload = async (fileKey: string, fileType: string) => {
    if (fileType === 'dxfFile') {
      if (dxfFileLoadingState[fileKey] !== 'Preparing Download' && dxfFileLoadingState[fileKey] !== 'Download') {
        const selectedCadFile = order.cadFiles.find((obj: ICadFile) => obj._id === fileKey);
        if (selectedCadFile?.expressAIUpload && selectedCadFile?.expressAIEmailSent) {
          setShowExpressAIWarning(true);
          setCadFile(selectedCadFile);
        } else {
          // Check if direct download is available (dxfS3Url exists and dxfUpdatedAt <= dxfCreatedAt)
          const directUrlResult = await dispatch(GetDxfDirectUrlAction(fileKey, includeEmptyLayers[fileKey]));
          if (directUrlResult.directDownload && directUrlResult.url) {
            // Direct download available - download immediately
            window.open(directUrlResult.url, '_self');
            (window as any)?.pendo?.track('Download DXF', {
              filename: getFileName(directUrlResult.url),
              filesize: `${((directUrlResult.size ?? 0) / 1000000).toFixed(2)} MB`,
              projectId: order.ownerProject,
              dxfType: expressAICreditAcreage > 0 ? 'express_ai' : 'QC',
              directDownload: true,
            });
          } else {
            // No direct download - prepare download
            setDxfFileLoadingState((prev) => ({ ...prev, [fileKey]: 'Preparing Download' }));
            await dispatch(SetDownloadFilesStateAction(fileKey, { ...fileDownloadCheckboxState, dxfFile: true, geoJson: false }));
            await dispatch(DownloadPostgisDxfAction(orderId, fileKey, includeEmptyLayers[fileKey], { ...fileDownloadCheckboxState, dxfFile: true, geoJson: false }));
          }
        }
      } else if (dxfFileLoadingState[fileKey] === 'Download') {
        await handleFileDownload(
          fileKey,
          'dxfFile',
          downloadFileStatuses,
          setDxfFileLoadingState,
          ClearDownloadFilesAction,
          'Download DXF',
          { dxfType: expressAICreditAcreage > 0 ? 'express_ai' : 'QC' }
        );
      }
    }

    if (fileType === 'geoJson') {
      setGeoJsonFileLoadingState((prev) => ({ ...prev, [fileKey]: 'Preparing Download' }));
      if (geoJsonFileLoadingState[fileKey] !== 'Preparing Download' && geoJsonFileLoadingState[fileKey] !== 'Download') {
        await dispatch(SetGeoJsonDownloadStatusAction(fileKey, { ...fileDownloadCheckboxState, geoJson: true, dxfFile: false }));
        await dispatch(DownloadPostgisDxfAction(orderId, fileKey, includeEmptyLayers[fileKey], { ...fileDownloadCheckboxState, geoJson: true, dxfFile: false }));
      } else if (geoJsonFileLoadingState[fileKey] === 'Download') {
        await handleFileDownload(
          fileKey,
          'geoJson',
          downloadGeoJsonFileStatuses,
          setGeoJsonFileLoadingState,
          ClearDownloadFilesAction,
          'Download GeoJson'
        );
      }
    }
  };

  const renderFileSection = (title: string, files: any[], downloadAction: (file: any) => void) => (
    files.length > 0 && (
      <Box sx={styles.checkboxContainer}>
        <Typography sx={styles.title} variant="h4">{title}</Typography>
        {files.map((file) => (
          <Box sx={{ ...styles.fileContainer, ...styles.fileContainerHover }} key={file._id}>
            <Typography>{getFileName(file.filename)}</Typography>
            <IconButton onClick={() => {
              downloadAction(file);
              (window as any)?.pendo?.track('Download Output', { filename: getFileName(file.filename) });
            }}>
              <img src={downloadIcon} alt="Download" />
            </IconButton>
          </Box>
        ))}
      </Box>
    )
  );

  return (
    <>
      <Snackbar sx={styles.snackBar} open={showSnackbar} autoHideDuration={10000} onClose={handleClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" sx={styles.alert} color="info"> Your file(s) are ready to download</Alert>
      </Snackbar>
      <Dialog id={id} open={open} sx={styles.downloadPanelDialog}>
        <Paper sx={styles.downloadPanel}>
          <Box sx={styles.header}>
            <Typography sx={styles.title} variant="h4">Export</Typography>
            <WideLightTooltip
              placement="top"
              arrow
              title={(
                <Box>
                  Please review
                  {' '}
                  <a style={styles.link} href="http://support.airworks.io/Workaround-for-exported-DXFs-in-AutoCAD.2406023169.html" target="_blank" rel="noreferrer">these steps</a>
                  {' '}
                  on importing an AirWorks dxf into AutoCAD.
                  {orderedGeoJson && (
                    <Box>
                      {' '}
                      <br />
                      {' '}
                      Your .geojson file will download in the WGS84 coordinate system.
                      {' '}
                    </Box>
                  )}
                </Box>
              )}
            >
              <HelpIcon sx={styles.icon} />
            </WideLightTooltip>
          </Box>
          {isAnyFilePreparing && (
            <Box sx={styles.prepareDownloadInfo}>
              <InfoIcon sx={styles.infoIcon} />
              <Typography sx={styles.prepareDownloadInfoText}>Your download has started. Feel free to keep working while we prepare your files.</Typography>
            </Box>
          )}
          <Box sx={styles.description}>
            <Typography>
              Select the file(s) to export and click on
              {' '}
              <b>"Download"</b>
              . If preparation is needed, a spinner will appear. Once ready, a blue dot will appear next to the Download icon.
            </Typography>
          </Box>
          {cadPresent && (
            <Box sx={styles.checkboxContainer}>
              <Typography sx={styles.title} variant="h4">CAD Files</Typography>
              {cadFiles.length > 0 && (
                cadFiles.map((file) => {
                  const fileKey = file.siteId;
                  return (
                    <Box key={fileKey} sx={{ ...styles.fileContainer, ...styles.fileContainerHover }}>
                      <Box sx={styles.fileDetails}>
                        <Typography>{file.fileName}</Typography>
                        <FormControlLabel
                          control={(
                            <Checkbox sx={styles.smallCheckbox} color="primary" checked={includeEmptyLayers[fileKey]} name={fileKey} onChange={handleIncludeEmptyLayersCheckboxChange} disabled={dxfFileLoadingState[fileKey] === 'Preparing Download'} />
                          )}
                          label="Include empty layers"
                          sx={styles.emptyLayers}
                        />
                      </Box>
                      <Button
                        disabled={dxfFileLoadingState[fileKey] === 'Preparing Download'}
                        onClick={() => handlePrepareAndDownload(fileKey, 'dxfFile')}
                        sx={styles.downloadButton}
                        color="primary"
                      >
                        {dxfFileLoadingState[fileKey] === 'Preparing Download' && (
                          <CircularProgress size={16} sx={styles.buttonProgress} color="primary" />
                        )}
                        {dxfFileLoadingState[fileKey] === 'Download' && (
                          <Badge color="primary" overlap="circle" variant="dot">
                            <IconButton
                              aria-label="Download"
                              title="Download"
                              color="primary"
                            >
                              <img src={downloadIcon} alt="Download" />
                            </IconButton>
                          </Badge>
                        )}
                        {((dxfFileLoadingState[fileKey] !== 'Preparing Download') && (dxfFileLoadingState[fileKey] !== 'Download')) && 'Download'}
                      </Button>
                    </Box>
                  );
                })
              )}

              {/* check if the user ordered geojson for this order and this is not an express AI dxf */}
              {orderedGeoJson && !cadFile?.expressAIUpload && (
                cadFiles.map((file) => {
                  const geoJsonKey = file?.siteId;
                  return (
                    <Box key={geoJsonKey} sx={{ ...styles.fileContainer, ...styles.fileContainerHover }}>
                      <Box sx={styles.fileDetails}>
                        <Typography>{file?.fileName?.replace('.dxf', '.geojson')}</Typography>
                        <FormControlLabel
                          control={(
                            <Checkbox sx={styles.smallCheckbox} color="primary" checked={includeEmptyLayers[geoJsonKey]} name={geoJsonKey} onChange={handleIncludeEmptyLayersCheckboxChange} disabled={geoJsonFileLoadingState[geoJsonKey] === 'Preparing Download'} />
                          )}
                          label="Include empty layers"
                          sx={styles.emptyLayers}
                        />
                      </Box>
                      <Button
                        disabled={geoJsonFileLoadingState[geoJsonKey] === 'Preparing Download'}
                        onClick={() => handlePrepareAndDownload(geoJsonKey, 'geoJson')}
                        sx={{ ...styles.downloadButton, ...(geoJsonFileLoadingState[geoJsonKey] === 'Prepare' ? { width: '5rem' } : {}) }}
                        color="primary"
                      >
                        {geoJsonFileLoadingState[geoJsonKey] === 'Preparing Download' && (
                          <CircularProgress size={16} sx={styles.buttonProgress} color="primary" />
                        )}
                        {geoJsonFileLoadingState[geoJsonKey] === 'Download' && (
                          <IconButton
                            aria-label="Download"
                            title="Download"
                            color="primary"
                          >
                            <img src={downloadIcon} alt="Download" />
                          </IconButton>
                        )}
                        {((geoJsonFileLoadingState[geoJsonKey] !== 'Preparing Download') && (geoJsonFileLoadingState[geoJsonKey] !== 'Download')) && 'Prepare'}
                      </Button>
                    </Box>
                  );
                })
              )}
            </Box>
          )}
          {renderFileSection('LAS Files', lasFiles, (file) => dispatch(DownloadLASAction(orderId, file.filename.split('/').pop())))}
          {renderFileSection('Surface Files', surfaceFiles, (file) => dispatch(DownloadSurfaceFileAction(file._id, orderId)))}
          {renderFileSection('Shape Files', shapeFiles, (file) => dispatch(DownloadShpFileAction(file._id, orderId)))}
          {renderFileSection('DEM Files', demFiles, (file) => dispatch(DownloadDeliverableFileAction(file._id, orderId)))}
          {renderFileSection('DTM Files', dtmFiles, (file) => dispatch(DownloadDeliverableFileAction(file._id, orderId)))}
          {renderFileSection('DWG Files', dwgFiles, (file) => dispatch(DownloadDeliverableFileAction(file._id, orderId)))}
          {renderFileSection('DGN Files', dgnFiles, (file) => dispatch(DownloadDeliverableFileAction(file._id, orderId)))}
          {renderFileSection('GeoDB Files', geoDbFiles, (file) => dispatch(DownloadGeoDbFileAction(file._id, orderId)))}
          <Box sx={styles.buttonContainer}>
            <Button
              sx={styles.cancelButton}
              onClick={handleCloseDownloadDialog}
            >
              Close
            </Button>
          </Box>
        </Paper>
      </Dialog>
      <Dialog
        open={showExpressAIWarning}
        onClose={() => { 
          setShowExpressAIWarning(false);
          setCadFile(undefined);
        }}
        sx={styles.dialog}
      >
        <Box sx={styles.dialogContent}>
          <Box sx={styles.warningTitle}>
            <ErrorIcon sx={styles.warningIcon} />
            <Typography variant="h3">Export Layer</Typography>
          </Box>
          <DialogContent>
            <Typography display="inline">
              This layer was generated by the
              {' '}
              <strong>AirWorks Autonomous Pipeline</strong>
              {' '}
              and has not yet had its quality assessed. Your final output will be ready by
              {' '}
            </Typography>
            <Typography sx={styles.deliveryDate} display="inline">
              {moment(deliveryEstimate).format('LL')}
              .
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setShowExpressAIWarning(false);
              setCadFile(undefined);
            }}>Close</Button>
            <Button
              variant="text"
              color="primary"
              onClick={confirmDxfExport}
            >
              {dialogSpinner ? (
                <CircularProgress size={16} sx={styles.expressButtonProgress} />
              ) : (
                <span>Export Anyway</span>
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
};

export default DownloadPanel;
